import { create } from 'zustand'
import { devtools } from 'zustand/middleware'
import { trpcClient } from '../../../utils/trpc'
import type { CycleSearchOutput } from '../../cycles/types'

interface OrganizationCyclesState {
	data: CycleSearchOutput[]
	isLoading: boolean
	error: string | null
	pagination: {
		page: number
		perPage: number
		total: number
		totalInPage: number
	}
	searchParams: {
		search: string
		sortBy: string
		order: 'asc' | 'desc'
	}
	organizationId: string | null

	fetchCycles: (organizationId: string) => Promise<void>
	setPage: (page: number) => void
	setPerPage: (perPage: number) => void
	setSearch: (search: string) => void
	setSort: (sortBy: string, order: 'asc' | 'desc') => void
	setOrganizationId: (id: string) => void
	reset: () => void
}

const initialPagination = {
	page: 1,
	perPage: 25,
	total: 0,
	totalInPage: 0,
}

const initialSearchParams = {
	search: '',
	sortBy: 'name',
	order: 'asc' as const,
}

export const useOrganizationCyclesStore = create<OrganizationCyclesState>()(
	devtools(
		(set, get) => ({
			data: [],
			isLoading: false,
			error: null,
			pagination: { ...initialPagination },
			searchParams: { ...initialSearchParams },
			organizationId: null,

			fetchCycles: async (organizationId: string) => {
				const { pagination, searchParams } = get()
				set({ isLoading: true, error: null, organizationId })

				try {
					const result = await trpcClient.admin.cycles.search.query({
						organizationId,
						page: pagination.page,
						perPage: pagination.perPage,
						search: searchParams.search,
						sortBy: searchParams.sortBy,
						order: searchParams.order,
					})

					set({
						data: result.data,
						pagination: {
							page: result.meta.page,
							perPage: result.meta.perPage,
							total: result.meta.total,
							totalInPage: result.meta.totalInPage,
						},
						isLoading: false,
					})
				} catch (error) {
					set({
						error: error instanceof Error ? error.message : 'Failed to fetch cycles',
						isLoading: false,
					})
				}
			},

			setPage: (page: number) => {
				const { organizationId } = get()
				set((state) => ({
					pagination: { ...state.pagination, page },
				}))
				if (organizationId) {
					get().fetchCycles(organizationId)
				}
			},

			setPerPage: (perPage: number) => {
				const { organizationId } = get()
				set((state) => ({
					pagination: { ...state.pagination, perPage, page: 1 },
				}))
				if (organizationId) {
					get().fetchCycles(organizationId)
				}
			},

			setSearch: (search: string) => {
				const { organizationId } = get()
				set((state) => ({
					searchParams: { ...state.searchParams, search },
					pagination: { ...state.pagination, page: 1 },
				}))
				if (organizationId) {
					get().fetchCycles(organizationId)
				}
			},

			setSort: (sortBy: string, order: 'asc' | 'desc') => {
				const { organizationId } = get()
				set((state) => ({
					searchParams: { ...state.searchParams, sortBy, order },
				}))
				if (organizationId) {
					get().fetchCycles(organizationId)
				}
			},

			setOrganizationId: (id: string) => {
				set({ organizationId: id })
			},

			reset: () => {
				set({
					data: [],
					isLoading: false,
					error: null,
					pagination: { ...initialPagination },
					searchParams: { ...initialSearchParams },
					organizationId: null,
				})
			},
		}),
		{ name: 'organization-cycles-store' },
	),
)
